// GameLift Streams connection-state store (MV-1077).
//
// The GameCast SDK reports `clientConnection.connectionState` transitions via a
// callback that lives deep in the GameCast hook; the Report It snapshot capture
// runs in a different tree. This tiny module-scoped store bridges the two: the
// GameCast hook records every transition, and the snapshot reads the latest
// state + a short transition history at capture time. Repeated
// disconnected→connected cycles are the signal behind "the screen freezes every
// few seconds" reports that per-frame WebRTC stats cannot prove on their own.

import { StreamConnectionSnapshot } from './snapshotTypes';

// Enough to show a flapping connection without bloating the size-capped
// POST /reports snapshot body.
const MAX_TRANSITIONS = 20;

let transitions: Array<{ state: string; atIso: string }> = [];

/** Record an SDK connection-state transition (called from the GameCast hook). */
export function recordStreamConnectionState(
  state: string,
  now: () => Date = () => new Date(),
): void {
  transitions.push({ state, atIso: now().toISOString() });

  if (transitions.length > MAX_TRANSITIONS) {
    transitions = transitions.slice(-MAX_TRANSITIONS);
  }
}

/**
 * Latest connection state + recent transition history for the Report It
 * snapshot. Undefined until the first transition is recorded (e.g. a report
 * filed before any stream was started).
 */
export function getStreamConnectionSnapshot():
  | StreamConnectionSnapshot
  | undefined {
  if (transitions.length === 0) {
    return undefined;
  }

  const latest = transitions[transitions.length - 1];

  return {
    state: latest.state,
    changedAt: latest.atIso,
    recentTransitions: [...transitions],
  };
}

/** Test-only reset — the store is module-scoped for the page lifetime. */
export function resetStreamConnectionStateForTests(): void {
  transitions = [];
}
